import React, { useEffect, useRef, useState } from 'react';
import { AnniversaryHeroBadge } from './AnniversaryOffer';


const TechInterviewBanner = () => {
  const bannerRef = useRef(null);
  const particlesContainerRef = useRef(null);
  const statCardsRef = useRef(null);
  const featureItemsRef = useRef(null);
  const mainImageRef = useRef(null);
  const titleRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');

  // Typing effect for the LLM chat card
  useEffect(() => {
    const fullResponse = 'RAG fetches relevant context, then the LLM generates a grounded answer.';
    let i = 0;
    let timer;
    const type = () => {
      if (i <= fullResponse.length) {
        setTypedText(fullResponse.slice(0, i));
        i++;
        timer = setTimeout(type, 40);
      } else {
        timer = setTimeout(() => {
          i = 0;
          setTypedText('');
          timer = setTimeout(type, 500);
        }, 2600);
      }
    };
    timer = setTimeout(type, 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Set visibility for animations
    setIsVisible(true);

    // Create glowing neural nodes (particles)
    const createParticles = () => {
      const particlesContainer = particlesContainerRef.current;
      if (!particlesContainer) return;

      const particleCount = 40;

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.classList.add('mib_particle');

        const size = Math.random() * 3 + 1;
        particle.style.width = `${size}px`;
        particle.style.height = `${size}px`;

        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;

        particle.style.opacity = Math.random() * 0.5 + 0.2;

        const duration = Math.random() * 30 + 15;
        const delay = Math.random() * 10;
        particle.style.animationDuration = `${duration}s`;
        particle.style.animationDelay = `${delay}s`;

        particlesContainer.appendChild(particle);
      }
    };

    // Add 3D tilt effect
    const initTiltEffect = () => {
      const banner = bannerRef.current;
      if (!banner) return;

      const handleMouseMove = (e) => {
        const rect = banner.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const moveX = (x - centerX) / centerX;
        const moveY = (y - centerY) / centerY;

        // Apply to the AI dashboard
        if (mainImageRef.current) {
          mainImageRef.current.style.transform = `translateZ(40px) rotateX(${moveY * -4}deg) rotateY(${moveX * 4}deg)`;
        }

        // Apply to feature items
        if (featureItemsRef.current) {
          const features = featureItemsRef.current.querySelectorAll('.mib_feature_item');
          features.forEach((feature, index) => {
            const factor = 0.3 + (index * 0.1);
            feature.style.transform = `translateX(${moveX * 18 * factor}px)`;
          });
        }

        // Apply to stat cards
        if (statCardsRef.current) {
          const cards = statCardsRef.current.querySelectorAll('.mib_stat_card');
          cards.forEach((card, index) => {
            const factorX = index % 2 === 0 ? 1 : -1;
            const factorY = index < 2 ? 1 : -1;
            card.style.transform = `translateZ(20px) translateX(${moveX * 12 * factorX}px) translateY(${moveY * 12 * factorY}px)`;
          });
        }

        // Apply subtle movement to title
        if (titleRef.current) {
          titleRef.current.style.transform = `translateX(${moveX * 8}px) translateY(${moveY * 8}px)`;
        }
      };

      const handleMouseLeave = () => {
        if (mainImageRef.current) {
          mainImageRef.current.style.transform = 'translateZ(40px) rotateX(0deg) rotateY(0deg)';
        }

        if (featureItemsRef.current) {
          const features = featureItemsRef.current.querySelectorAll('.mib_feature_item');
          features.forEach(feature => {
            feature.style.transform = '';
          });
        }

        if (statCardsRef.current) {
          const cards = statCardsRef.current.querySelectorAll('.mib_stat_card');
          cards.forEach(card => {
            card.style.transform = 'translateZ(20px)';
          });
        }

        if (titleRef.current) {
          titleRef.current.style.transform = '';
        }
      };

      banner.addEventListener('mousemove', handleMouseMove);
      banner.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        banner.removeEventListener('mousemove', handleMouseMove);
        banner.removeEventListener('mouseleave', handleMouseLeave);
      };
    };

    // Initialize all effects
    createParticles();
    const cleanupTilt = initTiltEffect();

    // Cleanup
    return () => {
      cleanupTilt && cleanupTilt();

      if (particlesContainerRef.current) {
        while (particlesContainerRef.current.firstChild) {
          particlesContainerRef.current.removeChild(particlesContainerRef.current.firstChild);
        }
      }
    };
  }, []);

  const handleApplyButtonClick = (e) => {
    // Create ripple effect
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ripple = document.createElement('span');
    ripple.className = 'mib_ripple';

    ripple.style.width = ripple.style.height = Math.max(rect.width, rect.height) * 2.5 + 'px';
    ripple.style.left = x - ripple.clientWidth / 2 + 'px';
    ripple.style.top = y - ripple.clientHeight / 2 + 'px';

    button.appendChild(ripple);

    setTimeout(() => {
      ripple.remove();
    }, 600);
  };

  const headlineTags = ['AI & ML', 'Generative AI', 'Agentic AI', 'LLMs', 'RAG', 'Python', 'Deep Learning', 'LangChain', 'LangGraph'];

  return (
    <div className={`mib_container ${isVisible ? 'mib_loaded' : ''}`}>
      {/* Navigation bar */}
      <nav className="mib_navbar">
        <div className="mib_nav_left">
          <img src="https://logicmojo.com/assets/dist/new_index_design/images/logo.png" alt="Logo" className="mib_logo" />
          <div className="mib_brand_text">AI &amp; ML COURSE</div>
          <div className="mib_badge">NEW BATCH - 14th Nov</div>
        </div>

        <div className="mib_nav_right">
          <div className="mib_nav_links">
            <a target="_default" href="https://drive.google.com/file/d/1SzaTFL3QiyuRzbtjI7e0dzMDCnLxtr0A/view" className="mib_nav_link">COURSE BROCHURE</a>
            <a target="_default" href="http://localhost:3000/#Demo" className="mib_nav_link">DEMO SESSION</a>
            <a target="_default" href="https://www.logicmojo.com/#Review" className="mib_nav_link">REVIEW</a>
            <a target="_default" href="https://www.logicmojo.com/#Contact" className="mib_nav_link">CONTACT US</a>
          </div>

          <div className="mib_nav_buttons">
            <a target="_default" href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2026-06" className="mib_button mib_button_primary">REQUEST A CALL</a>
          </div>
        </div>
      </nav>

      {/* Main banner */}
      <div className="mib_banner" ref={bannerRef}>
        {/* Background elements */}
        <div className="mib_banner_bg">
          <div className="mib_bg_gradient"></div>
          <div className="mib_bg_grid"></div>
          <div className="mib_curve"></div>
          <div className="mib_glow mib_glow_1"></div>
          <div className="mib_glow mib_glow_2"></div>
          <div className="mib_glow mib_glow_3"></div>

          {/* Neural network lines + glowing nodes */}
          <svg className="mib_neural" viewBox="0 0 900 600" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <g className="mib_neural_lines">
              <line x1="70" y1="120" x2="220" y2="80" />
              <line x1="70" y1="120" x2="190" y2="270" />
              <line x1="220" y1="80" x2="360" y2="180" />
              <line x1="190" y1="270" x2="360" y2="180" />
              <line x1="190" y1="270" x2="320" y2="400" />
              <line x1="360" y1="180" x2="500" y2="120" />
              <line x1="360" y1="180" x2="480" y2="320" />
              <line x1="320" y1="400" x2="480" y2="320" />
              <line x1="500" y1="120" x2="650" y2="220" />
              <line x1="480" y1="320" x2="650" y2="220" />
              <line x1="480" y1="320" x2="620" y2="450" />
              <line x1="650" y1="220" x2="800" y2="150" />
              <line x1="650" y1="220" x2="620" y2="450" />
            </g>
            <g className="mib_neural_nodes">
              <circle cx="70" cy="120" r="4" />
              <circle cx="220" cy="80" r="5" />
              <circle cx="190" cy="270" r="4" />
              <circle cx="360" cy="180" r="6" />
              <circle cx="320" cy="400" r="4" />
              <circle cx="500" cy="120" r="5" />
              <circle cx="480" cy="320" r="6" />
              <circle cx="650" cy="220" r="5" />
              <circle cx="620" cy="450" r="4" />
              <circle cx="800" cy="150" r="5" />
            </g>
          </svg>

          <div className="mib_particles" ref={particlesContainerRef}></div>
        </div>

        <div className="mib_banner_content">
          <div className="mib_left_content">
            <div className="mib_eyebrow">
              <span className="mib_eyebrow_dot"></span>
              Become Job-Ready in AI, ML &amp; GenAI
            </div>

            <h1 className="mib_title" ref={titleRef}>
              <div className="mib_title_line">Master AI &amp; Machine Learning</div>
              <div className="mib_title_line">
                with <span className="mib_title_highlight">GenAI + Agentic AI</span>
              </div>
            </h1>

            <p className="mib_subtitle">
             LogicMojo build real world AI projects and gain hands on experience with LLMs, RAG, Machine Learning, Generative AI and Agentic AI workflows through live mentor led training, mock interviews, resume preparation and job referral support
            </p>

            <div className="mib_headline_tags">
              {headlineTags.map((tag) => (
                <span className="mib_headline_tag" key={tag}>{tag}</span>
              ))}
            </div>

            <div className="mib_features" ref={featureItemsRef}>
              <div className="mib_feature_item">
                <div className="mib_feature_icon">
                  <i className="mib_check_icon"></i>
                </div>
                <div className="mib_feature_text">
                  Learn Python, Machine Learning, Agentic AI &amp; Generative AI
                </div>
              </div>

              <div className="mib_feature_item">
                <div className="mib_feature_icon">
                  <i className="mib_check_icon"></i>
                </div>
                <div className="mib_feature_text">
                  Build LLM, RAG, Chatbot, AI Agent &amp; MLOps Projects
                </div>
              </div>

              <div className="mib_feature_item">
                <div className="mib_feature_icon">
                  <i className="mib_check_icon"></i>
                </div>
                <div className="mib_feature_text">
                  Get Resume Preparation, Mock Interviews &amp; Job Referrals
                </div>
              </div>

              <div className="mib_feature_item">
                <div className="mib_feature_icon">
                  <i className="mib_check_icon"></i>
                </div>
                <div className="mib_feature_text">
                  Flexible Weekday + Weekend Live Classes for Working Professionals
                </div>
              </div>
            </div>

            {/* Anniversary offer badge — sits above the primary CTA */}
            <AnniversaryHeroBadge />

            <div className="mib_cta_container">
              <a target="_default" href="https://calendly.com/logicmojo/schedule-call-back-from-experts-for-live-classes?month=2026-06" className="mib_apply_button" onClick={handleApplyButtonClick}>
                Apply For Live AI Classes
                <i className="mib_arrow_icon"></i>
              </a>
            </div>
          </div>

          <div className="mib_right_content">
            <div className="mib_ai_dashboard" ref={mainImageRef}>
              {/* Floating AI chips */}
              <div className="mib_ai_chips">
                <span className="mib_ai_chip mib_chip_1">LLM</span>
                <span className="mib_ai_chip mib_chip_2">RAG</span>
                <span className="mib_ai_chip mib_chip_3">Agents</span>
                <span className="mib_ai_chip mib_chip_4">GenAI</span>
                <span className="mib_ai_chip mib_chip_5">ML</span>
                <span className="mib_ai_chip mib_chip_6">Vector DB</span>
              </div>

              <div className="mib_ai_grid">
                {/* LLM Chat Interface */}
                <div className="mib_ai_card mib_card_chat">
                  <div className="mib_ai_card_head">
                    <span className="mib_ai_card_dot"></span>
                    LLM Chat Interface
                  </div>
                  <div className="mib_chat_msg mib_chat_user">What is RAG?</div>
                  <div className="mib_chat_msg mib_chat_bot">
                    {typedText}
                    <span className="mib_type_cursor"></span>
                  </div>
                </div>

                {/* RAG Pipeline */}
                <div className="mib_ai_card mib_card_rag">
                  <div className="mib_ai_card_head">
                    <span className="mib_ai_card_dot"></span>
                    RAG Pipeline
                  </div>
                  <div className="mib_pipeline">
                    <span className="mib_pipe_step">Data</span>
                    <span className="mib_pipe_arrow"></span>
                    <span className="mib_pipe_step">Embeddings</span>
                    <span className="mib_pipe_arrow"></span>
                    <span className="mib_pipe_step">Vector DB</span>
                    <span className="mib_pipe_arrow"></span>
                    <span className="mib_pipe_step">Answer</span>
                    <span className="mib_pipe_flow"></span>
                  </div>
                </div>

                {/* AI Agent Workflow */}
                <div className="mib_ai_card mib_card_agent">
                  <div className="mib_ai_card_head">
                    <span className="mib_ai_card_dot"></span>
                    AI Agent Workflow
                  </div>
                  <div className="mib_agent_loop">
                    <span className="mib_agent_step">Plan</span>
                    <span className="mib_agent_step">Tool Call</span>
                    <span className="mib_agent_step">Execute</span>
                    <span className="mib_agent_step">Improve</span>
                  </div>
                </div>

                {/* ML Model Dashboard */}
                <div className="mib_ai_card mib_card_ml">
                  <div className="mib_ai_card_head">
                    <span className="mib_ai_card_dot"></span>
                    ML Model Dashboard
                  </div>
                  <div className="mib_ml_metrics">
                    <div className="mib_ml_row">
                      <span>Accuracy</span>
                      <div className="mib_ml_bar"><i style={{ width: '94%' }}></i></div>
                    </div>
                    <div className="mib_ml_row">
                      <span>Loss</span>
                      <div className="mib_ml_bar"><i style={{ width: '18%' }}></i></div>
                    </div>
                    <div className="mib_ml_row">
                      <span>Prediction</span>
                      <div className="mib_ml_bar"><i style={{ width: '88%' }}></i></div>
                    </div>
                    <div className="mib_ml_foot">
                      <span className="mib_ml_status">Deployment</span>
                      <span className="mib_ml_live">LIVE</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mib_floating_badge">
                <div className="mib_badge_inner">
                  <span className="mib_badge_star">★</span>
                  <span className="mib_badge_text">GenAI + Agentic AI</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mib_stats_container" ref={statCardsRef}>
          <div className="mib_stat_card">
            <div className="mib_stat_icon mib_icon_projects"></div>
            <div className="mib_stat_content">
              <div className="mib_stat_value">10+ AI Projects</div>
              <div className="mib_stat_sublabel">LLMs, RAG, Agents &amp; ML</div>
            </div>
          </div>

          <div className="mib_stat_card">
            <div className="mib_stat_icon mib_icon_live"></div>
            <div className="mib_stat_content">
              <div className="mib_stat_value">Live Weekend Classes</div>
              <div className="mib_stat_sublabel">Mentor-led sessions for working professionals</div>
            </div>
          </div>

          <div className="mib_stat_card">
            <div className="mib_stat_icon mib_icon_mock"></div>
            <div className="mib_stat_content">
              <div className="mib_stat_value">Mock Interviews</div>
              <div className="mib_stat_sublabel">Resume prep + interview practice</div>
            </div>
          </div>

          <div className="mib_stat_card">
            <div className="mib_stat_icon mib_icon_referral"></div>
            <div className="mib_stat_content">
              <div className="mib_stat_value">Job Referral Support</div>
              <div className="mib_stat_sublabel">For eligible learners after project completion</div>
            </div>
          </div>
        </div>

        <div className="mib_emi_strip">
          <span className="mib_emi_icon">₹</span>
          <div className="mib_emi_text">
            <strong>Easy EMI Options</strong>
            <span>Flexible payment plans available</span>
          </div>
        </div>

        <div className="mib_decoration_elements">
          <div className="mib_circle mib_circle_1"></div>
          <div className="mib_circle mib_circle_2"></div>
          <div className="mib_square"></div>
          <div className="mib_dots">
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
            <div className="mib_dot"></div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default TechInterviewBanner;
